import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks, MoreHorizontal } from 'lucide-react';
import { DENSE_ACTION_CLASS } from '@/components/work/WorkActionControl';
import type { AppObjectNavigationState } from '@/components/apps/AppRecordsView';
import { setTopicsAside } from '@/lib/topic-plan';
import { retryWorkEvaluation } from '@/lib/work-actions';
import { GuidanceMotifItems } from './GuidanceMotifItems';
import { launchSessionHref, type TodayLaunch } from './today-launches';
import { RowIdentity } from './work-row-visuals';

const launchDay = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
const records = (count: number) => `${count} fiche${count > 1 ? 's' : ''}`;

/** The row's title: what is to do now, else where the objects' planning stands. */
export const launchTitle = (launch: Pick<TodayLaunch, 'recordIds' | 'planning' | 'failed'>): string =>
  launch.recordIds.length > 0 ? `Traiter ${records(launch.recordIds.length)}`
    : launch.failed.length > 0 ? `Suites indisponibles pour ${records(launch.failed.length)}`
      : `Suites en préparation pour ${records(launch.planning.length)}`;

/** The objects not counted in the title, in a short grey line. */
export const launchRest = (launch: Pick<TodayLaunch, 'recordIds' | 'planning' | 'failed'>): string | null => {
  const parts = [
    launch.planning.length > 0 && (launch.recordIds.length > 0 || launch.failed.length > 0) ? `${records(launch.planning.length)} en préparation` : null,
    launch.failed.length > 0 && launch.recordIds.length > 0 ? `${records(launch.failed.length)} sans suite` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/**
 * One launch of an app as one task: « Faire » opens its first object in séance,
 * where each object's actions are launched or set aside one after another.
 * Setting the launch aside closes all its actions with one motive. Objects
 * whose next actions are being planned or failed stay in the row; failed ones
 * are retried together (28/10/2026).
 */
export const TodayLaunchRow = ({ launch, onChange }: {
  launch: TodayLaunch;
  onChange?: () => void;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const count = launch.recordIds.length + launch.planning.length + launch.failed.length;
  const title = launchTitle(launch);
  const rest = launchRest(launch);
  const appHref = launch.appKey ? `/apps/${encodeURIComponent(launch.appKey)}` : `/tools/${encodeURIComponent(launch.flowId)}`;

  const setAside = async (motif: Parameters<typeof setTopicsAside>[0]['motif'], note: string | null) => {
    setPending(true);
    setError(null);
    try {
      await setTopicsAside({
        topics: launch.items.map((item) => item.row.topic),
        horizon: motif === 'done' ? 'done' : 'dropped',
        motif, note,
        workTitle: `${title} de « ${launch.flowTitle} »`,
      });
      setMenuOpen(false);
      onChange?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Ces travaux n’ont pas pu être écartés.');
    } finally {
      setPending(false);
    }
  };

  const retry = async () => {
    setPending(true);
    setError(null);
    const results = await Promise.allSettled(launch.failed.map((topic) => retryWorkEvaluation(topic.id)));
    const refused = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (refused) setError(refused.reason instanceof Error ? refused.reason.message : 'La préparation n’a pas pu être relancée.');
    setPending(false);
    onChange?.();
  };
  const retryButton = (className: string) => (
    <button type="button" disabled={pending} onClick={() => void retry()} title={launch.failed[0]?.preparation?.message ?? undefined} className={className}>
      Réessayer
    </button>
  );

  return (
    <article className="rounded-lg border border-domino-cardBorder bg-domino-white px-3.5 py-3 transition-colors hover:bg-domino-muted/30 sm:rounded-none sm:border-0">
      <div className="work-action-row">
        <div className="work-account min-w-0">
          <RowIdentity icon={ListChecks} label={launch.flowTitle} href={appHref}
            detail={launch.runId ? `Lancement${launch.launchedAt ? ` du ${launchDay(launch.launchedAt)}` : ''}` : undefined} />
        </div>
        <div className="work-menu">
          <div className="relative shrink-0">
            <button
              type="button"
              aria-label={`Options pour ${title}`}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((current) => !current)}
              className="inline-flex h-8 w-8 items-center justify-center rounded text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div role="menu" aria-label="Écarter le lancement" className="absolute right-0 top-9 z-20 w-56 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-elevated)]">
                <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-domino-inkSoft">Écarter les {count}</p>
                <GuidanceMotifItems disabled={pending} onPick={(motif, note) => void setAside(motif, note)} />
                {error && <p role="alert" className="px-3 py-1 text-xs text-domino-inkSoft">{error}</p>}
              </div>
            )}
          </div>
        </div>
        <div className="work-title flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-normal leading-5 text-domino-ink">{title}</h3>
            {(rest || (error && !menuOpen)) && (
              <p className="mt-0.5 text-xs leading-5 text-domino-inkSoft">
                {rest}
                {rest && launch.recordIds.length > 0 && launch.failed.length > 0 && <> · {retryButton('text-domino-aubergine hover:underline disabled:opacity-50')}</>}
                {error && !menuOpen && <span role="alert" className="block">{error}</span>}
              </p>
            )}
          </div>
        </div>
        <div className="work-command min-w-0">
          {launch.recordIds.length > 0 ? (
            <Link to={launchSessionHref(launch)} state={{ fromWork: true } satisfies AppObjectNavigationState}
              className={`inline-flex min-h-8 max-w-full items-center rounded-lg px-3 py-1 text-xs font-semibold leading-4 ${DENSE_ACTION_CLASS}`}>
              Faire
            </Link>
          ) : launch.failed.length > 0
            ? retryButton(`inline-flex min-h-8 max-w-full items-center rounded-lg px-3 py-1 text-xs font-semibold leading-4 disabled:opacity-50 ${DENSE_ACTION_CLASS}`)
            : null}
        </div>
      </div>
    </article>
  );
};
